import { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import type { SalesOrderDto, AddressDto } from '../types/salesOrder.types';
import { useSalesOrderStore } from '../store/salesOrderStore';
import {
  AddressRow,
  ChecklistRow,
  Grid,
  Row,
  Section,
  type ResolvedAddr,
} from './readiness/reviewFields';
import { SalesOrderCorrectPriceButton } from './SalesOrderCorrectPriceButton';

// 86d3bujyr / 86d3ch831 — sectioned summary-of-record for a read-only/posted SO, rendered
// inline in place of the editable Order Details cabinet. A posting-metadata header (NetSuite
// tran id + DOP # + "Posted to NetSuite" + posted by/at) over the COMPLETE Order Details
// field set, grouped into the same two sections as the pre-submit review modal so a
// coordinator can cross-check the DOP record against the posted NetSuite SO. Every field is
// shown even when empty ("—").

const SEPARATE_BILLING_LABELS: Record<string, string> = {
  '1': 'Separate Billing',
  '2': 'No',
  '3': 'Freight Incorporated',
};

function waivedLabel(id: string | null | undefined): string | null {
  if (id === '1') return 'Yes';
  if (id === '2') return 'No';
  return null;
}

// Resolve an address to { label, full text }. Prefer the FROZEN denormalized columns (the
// as-posted record); fall back to the live address book by id for SOs submitted before the
// snapshot-freeze landed (those store only the id).
function resolveAddrSummary(
  addresseeName: string | null,
  lines: Array<string | null>,
  addressId: string | null,
  addresses: AddressDto[],
): ResolvedAddr {
  const text = lines.filter((l): l is string => !!l && l.trim().length > 0).join(', ');
  if (addresseeName || text) return { label: addresseeName ?? null, text };
  if (addressId) {
    const found = addresses.find((a) => a.internalId === addressId);
    if (found) {
      const t = (found.addrText?.trim() || '').replace(/\s*\n\s*/g, ', ');
      return { label: found.label?.trim() || found.addressee?.trim() || null, text: t };
    }
  }
  return null;
}

// Office contacts as clear name · number pairs; only the filled ones are shown (a name or
// a number counts). Pairing keeps the association the flat 3-col grid lost (86d3ch831).
function ContactsRow({
  contacts,
}: {
  contacts: Array<{ name: string | null; number: string | null }>;
}) {
  const filled = contacts.filter(
    (c) => (c.name && c.name.trim()) || (c.number && c.number.trim()),
  );
  return (
    <div className="col-span-full">
      <dt className="text-xs text-muted-foreground">
        Office {filled.length === 1 ? 'contact' : 'contacts'}
      </dt>
      <dd className="font-medium">
        {filled.length ? (
          <div className="flex flex-col gap-0.5">
            {filled.map((c, i) => (
              <span key={i}>
                {c.name?.trim() || '—'}
                {c.number?.trim() && (
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {' '}
                    · {c.number.trim()}
                  </span>
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </dd>
    </div>
  );
}

// Condensed key:value chip for the collapsed summary header (truncates long values).
function CondensedPair({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate font-medium" title={value?.trim() || undefined}>
        {value?.trim() || '—'}
      </dd>
    </div>
  );
}

// "Jun 10, 2026, 5:09 PM" — matches Quotation's formatDateTime12h.
function formatPostedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function SalesOrderReadOnlySummary({ draft }: { draft: SalesOrderDto }) {
  // The address book for the draft's Deliver/Bill-to company is loaded on open; a saved
  // address resolves here, a custom one falls back to the frozen denormalized columns.
  const addresses = useSalesOrderStore((s) => s.customerAddresses);
  // Collapsed by default → shows just the essentials (Customer · Project · PO); expand for
  // the full cross-check record (86d3ch831).
  const [expanded, setExpanded] = useState(false);
  const shipping = resolveAddrSummary(
    draft.shippingAddresseeName,
    [draft.shippingAddrLine1, draft.shippingAddrLine2, draft.shippingAddrLine3],
    draft.shippingAddressId,
    addresses,
  );
  const billing = resolveAddrSummary(
    draft.billingAddresseeName,
    [draft.billingAddrLine1, draft.billingAddrLine2, draft.billingAddrLine3],
    draft.billingAddressId,
    addresses,
  );

  // Header only once truly posted (netSuiteTranId lands at submit) — NOT during Posting,
  // where salesOrderNumber is still the DR- draft number.
  const posted = !!draft.netSuiteTranId;

  const poDisplay = draft.customerPoNumber
    ? draft.waitingForOfficialPo
      ? `${draft.customerPoNumber} · awaiting official PO`
      : draft.customerPoNumber
    : draft.waitingForOfficialPo
      ? 'Awaiting official PO'
      : null;
  const overallPrice =
    draft.overallPricePerKg != null ? `₱${draft.overallPricePerKg.toFixed(2)}` : null;
  const freightLine =
    draft.includeFreightLine == null
      ? 'Default (by billing type)'
      : draft.includeFreightLine
        ? 'Included on SO'
        : 'Omitted';
  const deliveryReqs = [
    { label: 'Trailer', on: draft.isTrailer },
    { label: 'M-F', on: draft.isMf },
    { label: 'PPE', on: draft.isPpeRequired },
    { label: 'Permit', on: draft.isPermitRequired },
  ];
  const flags = [
    { label: 'Priority', on: draft.isPriority },
    { label: 'Spot', on: draft.isSpotOrder },
    { label: 'Reprint', on: draft.isReprint },
  ];
  const alerts = [
    { label: 'Invoice e-copy', on: draft.alertInvoiceECopy },
    { label: 'Manual computation', on: draft.alertManualComputation },
    { label: 'MC 2 copies', on: draft.alertMc2Copies },
    { label: 'DR picture', on: draft.alertDrPicture },
    { label: 'DR attachment', on: draft.alertDrAttachment },
    { label: 'BRN traceability', on: draft.alertBrnTraceability },
  ];

  const gridCols = 'lg:grid-cols-3';

  return (
    <div className="space-y-4 border-y bg-muted/30 px-4 py-4">
      {posted && (
        <div className="border-b border-border/60 pb-3">
          {/* Top row — NetSuite + DOP document numbers (left) + Correct Price tool (right, 86d3gdj5q). */}
          <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                NetSuite
              </span>
              {draft.netSuiteUrl ? (
                <a
                  href={draft.netSuiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-info underline decoration-info/40 underline-offset-2 transition-colors hover:decoration-info"
                  title={`Open ${draft.netSuiteTranId} in NetSuite`}
                >
                  {draft.netSuiteTranId}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="font-mono text-sm font-semibold">{draft.netSuiteTranId}</span>
              )}
            </div>
            {draft.salesOrderNumber && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  DOP
                </span>
                <span className="font-mono text-sm font-semibold">{draft.salesOrderNumber}</span>
              </div>
            )}
          </div>
          <SalesOrderCorrectPriceButton draft={draft} />
          </div>
          {/* Second row — status badge + posted-by metadata. Smaller, muted. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
            <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Posted to NetSuite
            </span>
            {draft.postedAt && (
              <span className="text-muted-foreground tabular-nums">
                <span className="font-medium">Posted:</span> {formatPostedAt(draft.postedAt)}
              </span>
            )}
            {draft.postedByName && (
              <span className="text-muted-foreground">
                <span className="font-medium">By:</span> {draft.postedByName}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        {expanded ? (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Order details
          </span>
        ) : (
          <dl className="flex min-w-0 flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
            <CondensedPair label="Customer" value={draft.customerName} />
            <CondensedPair label="Project" value={draft.projectName} />
            <CondensedPair label="PO #" value={draft.customerPoNumber} />
          </dl>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? 'Hide details' : 'Show details'}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4">
      <Section title="Customer & Contract">
        <Grid className={gridCols}>
          <Row label="Firm Offer" value={draft.linkedFirmOfferTranId} mono />
          <Row label="Customer" value={draft.customerName} />
          <Row label="Head Office" value={draft.headMainOffice} />
          <Row
            label="Deliver/Bill-to"
            value={draft.doCustCompanyName}
            className="col-span-full lg:col-span-2"
          />
          <Row label="Terms" value={draft.termsName} />
          <Row label="Project" value={draft.projectName} className="col-span-full" />
          <Row label="Sales rep" value={draft.salesRepName} />
          <Row label="Sales coordinator" value={draft.salesCoordName} />
          <Row label="Date" value={draft.date} />
          <Row label="Customer PO #" value={poDisplay} />
          <Row label="PO Expiry" value={draft.poExpiryDate} />
          <Row label="Contract Expiry" value={draft.contractExpiryDate} />
          <Row label="Overall price / kg" value={overallPrice} mono />
          <Row
            label="Sub-PO order list"
            value={draft.subPoOrderlistLink}
            className="col-span-full lg:col-span-2"
          />
          <Row label="Invoice-to letter" value={draft.invoiceToLetter} />
        </Grid>
      </Section>

      <Section title="Shipping & Delivery">
        <Grid className={gridCols}>
          <AddressRow label="Shipping" addr={shipping} />
          <AddressRow label="Billing" addr={billing} fallback="Defaults to customer" />
          <Row
            label="Separate Billing"
            value={SEPARATE_BILLING_LABELS[draft.separateBilling] ?? draft.separateBilling}
          />
          <Row label="Waived Freight" value={waivedLabel(draft.waivedFreight)} />
          {/* 86d3fteqa: Freight/Ton is the rate that drove the charge; Actual is reference only. */}
          <Row
            label="Freight / Ton"
            value={
              draft.freightChargePerTon != null ? `₱${draft.freightChargePerTon.toFixed(2)}` : null
            }
            mono
          />
          <Row
            label="Actual Freight/MT (ref)"
            value={draft.actualFreightPerMt != null ? String(draft.actualFreightPerMt) : null}
          />
          <Row label="Freight line" value={freightLine} />
          <Row label="Discounted Freight" value={draft.isDiscountedFreight ? 'Yes' : 'No'} />
          <Row label="Discount details" value={draft.discountDetails} />
          <Row label="Delivery Due" value={draft.deliveryDueDate} />
          <Row label="Schedule" value={draft.scheduleName} />
          <ContactsRow
            contacts={[
              { name: draft.officeContact1, number: draft.contactNumber1 },
              { name: draft.officeContact2, number: draft.contactNumber2 },
              { name: draft.officeContact3, number: draft.contactNumber3 },
            ]}
          />
          <Row
            label="Authorized Receivers"
            value={draft.authorizedReceivers}
            className="col-span-full"
          />
          <Row
            label="Other Instructions"
            value={draft.otherInstructions}
            className="col-span-full"
          />
          <Row label="Other Remarks" value={draft.soRemarks} className="col-span-full" />
          <ChecklistRow label="Delivery requirements" items={deliveryReqs} />
          <ChecklistRow label="Flags" items={flags} />
          <ChecklistRow label="Document alerts" items={alerts} />
        </Grid>
      </Section>
        </div>
      )}
    </div>
  );
}
